<template>
  <transition name="user-chat-slide">
    <div v-show="visible" class="user-chat-window elevation-12" :style="windowStyle">
      <!-- Header -->
      <div class="user-chat-header" :class="minimized ? 'user-chat-header--minimized' : ''">
        <v-icon dark small class="mr-2">mdi-account-circle</v-icon>
        <span class="user-chat-header-title text-truncate">{{ customerName }}</span>
        <v-spacer />
        <v-btn icon dark x-small @click="$emit('toggle-minimize', chatId)" class="mr-1">
          <v-icon x-small>{{ minimized ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
        <v-btn icon dark x-small @click="$emit('close', chatId)">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Body (hidden when minimized) -->
      <template v-if="!minimized">
        <!-- Summary banner -->
        <div v-if="summary" class="user-chat-summary">
          <v-icon x-small color="orange darken-2" class="mr-1">mdi-information-outline</v-icon>
          <span>{{ summary }}</span>
        </div>

        <!-- Messages -->
        <div ref="messagesContainer" class="user-chat-messages">
          <div v-if="messages.length === 0" class="user-chat-empty">
            <v-icon size="36" color="grey lighten-1">mdi-message-text-outline</v-icon>
            <p class="grey--text mt-2 text-body-2">Start the conversation...</p>
          </div>
          <div v-for="(msg, i) in messages" :key="i" class="chat-bubble-row"
            :class="msg.role === 'agent' ? 'justify-end' : 'justify-start'">
            <div class="chat-bubble-wrapper" :class="msg.role === 'agent' ? 'align-end' : 'align-start'">
              <span v-if="msg.name" class="chat-bubble-user"
                :class="msg.role === 'agent' ? 'text-right' : 'text-left'">{{ msg.name }}</span>
              <div class="chat-bubble"
                :class="msg.role === 'agent' ? 'chat-bubble--agent primary' : 'chat-bubble--customer grey lighten-3'">
                <span :class="[msg.role === 'agent' ? 'white--text' : '', 'chat-text']"
                  v-html="markdown(msg.text)"></span>
              </div>
            </div>
          </div>
          <div v-if="isLoading" class="chat-bubble-row justify-end">
            <div class="chat-bubble chat-bubble--agent grey lighten-3">
              <v-progress-circular indeterminate size="16" width="2" color="primary" />
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="user-chat-input-area">
          <v-text-field v-model="inputText" placeholder="Type a message..." hide-details dense outlined
            class="user-chat-input" @keydown.enter="sendMessage">
            <template #append>
              <v-btn icon small color="primary" :disabled="!inputText.trim()" @click="sendMessage">
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </div>
      </template>
    </div>
  </transition>
</template>

<script>
import SRConst from '@/helpers/Constants/SignalRConstants.js';
import { marked } from 'marked';

export default {
  name: 'UserChatWindow',
  props: {
    chatId: {
      type: [String, Number],
      required: true
    },
    customerId: {
      type: [String, Number],
      required: true
    },
    userId: {
      type: [String, Number],
      default: null
    },
    customerName: {
      type: String,
      default: 'Customer'
    },
    summary: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: true
    },
    minimized: {
      type: Boolean,
      default: false
    },
    /** Horizontal offset in pixels from the right edge, set by the container */
    offsetRight: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      inputText: '',
      messages: [],
      isLoading: false
    };
  },
  computed: {
    windowStyle() {
      return {
        right: this.offsetRight + 'px'
      };
    }
  },
  watch: {
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    }
  },
  mounted() {
    // Subscribe to customer chat messages scoped to this session
    this.$store.dispatch('NotificationStore/subscribe', [
      SRConst.EventKeys.Chat,
      this.onIncomingMessage
    ]);
  },
  beforeDestroy() {
    this.$store.dispatch('NotificationStore/unsubscribe', [
      SRConst.EventKeys.Chat,
      this.onIncomingMessage
    ]);
  },
  methods: {
    markdown(text) {
      return marked(text);
    },
    onIncomingMessage(args) {
      // Only handle messages aimed at this chat session
      console.log("INCOMING CHAT MESSAGE:", args);
      if (args.response.channel !== `customer:${this.userId}`) return;

      if (args.response && args.response.type === 2 /* channel */) {
        const { text, role, name } = args.response.content;
        this.messages.push({ role: role || 'customer', text, name });
        this.isLoading = false;
      }
    },
    sendMessage() {
      const text = this.inputText.trim();
      if (!text) return;

      this.inputText = '';
      this.isLoading = true;

      this.$store.dispatch('NotificationStore/message', {
        Mode: 'Chat',
        Channel: `customer:${this.userId}`,
        Content: { text }
      });
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }
};
</script>

<style scoped>
.user-chat-window {
  position: fixed;
  bottom: 80px;
  width: 340px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  z-index: 998;
}

.user-chat-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: #455a64;
  color: #fff;
  flex-shrink: 0;
  cursor: pointer;
}

.user-chat-header--minimized {
  border-radius: 12px;
}

.user-chat-header-title {
  font-size: 14px;
  font-weight: 600;
  max-width: 180px;
}

.user-chat-summary {
  padding: 8px 12px;
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
  font-size: 12px;
  color: #6d4c00;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.user-chat-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 12px;
  min-height: 200px;
  max-height: 300px;
}

.user-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 180px;
}

.chat-bubble-row {
  display: flex;
  margin-bottom: 8px;
}

::v-deep .chat-text p {
  margin-bottom: 0px;
}

.chat-bubble {
  padding: 8px 14px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
}

.chat-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 80%;
}

.chat-bubble-wrapper.align-end {
  align-items: flex-end;
}

.chat-bubble-wrapper.align-start {
  align-items: flex-start;
}

.chat-bubble-user {
  font-size: 11px;
  color: #757575;
  margin-bottom: 2px;
  padding: 0 4px;
}

.chat-bubble--agent {
  border-bottom-right-radius: 4px;
}

.chat-bubble--customer {
  border-bottom-left-radius: 4px;
}

.user-chat-input-area {
  padding: 8px 10px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.user-chat-input {
  font-size: 13px;
}

/* Slide transition */
.user-chat-slide-enter-active,
.user-chat-slide-leave-active {
  transition: all 0.25s ease;
}

.user-chat-slide-enter,
.user-chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
